import React from 'react';
import { useNavigate } from 'react-router-dom';

function Privacy() {
  const navigate = useNavigate();
  const lastUpdated = 'April 11, 2025';

  const sections = [
    {
      title: 'Information We Collect',
      content: [
        {
          subtitle: 'Account Information',
          text: 'When you create a Vyblo account, we collect your name, email address, and password. Providers may also provide a business name, location, profile photo, and service descriptions.',
        },
        {
          subtitle: 'Booking Information',
          text: 'We collect information related to appointments you book or receive, including service type, date, time, and payment status.',
        },
        {
          subtitle: 'Payment Information',
          text: 'Payments are processed by Stripe. Vyblo does not store your full credit card number or bank account details. We receive confirmation of payment and payout status from Stripe.',
        },
        {
          subtitle: 'Communications',
          text: 'When you send messages through Vyblo\'s inbox, we store those messages to deliver them and maintain your conversation history.',
        },
        {
          subtitle: 'Phone Number',
          text: 'If you provide a phone number in your profile, it is used solely to send you appointment-related SMS notifications. We do not share your phone number with third parties for marketing.',
        },
        {
          subtitle: 'Usage Data',
          text: 'We collect standard usage data such as browser type, device type, pages visited, and actions taken on the platform to improve our service.',
        },
      ],
    },
    {
      title: 'How We Use Your Information',
      content: [
        { subtitle: 'To provide the service', text: 'We use your information to create and manage your account, process bookings, send confirmations, and facilitate communication between clients and providers.' },
        { subtitle: 'Notifications', text: 'We use your email and phone number (if provided) to send booking confirmations, reminders, cancellations, and rescheduling alerts. You can opt out of SMS notifications in your account settings.' },
        { subtitle: 'Payouts', text: 'Provider payout information is shared with Stripe to process earnings transfers. Stripe\'s privacy policy governs their handling of this data.' },
        { subtitle: 'Platform improvement', text: 'Aggregated, anonymized usage data helps us understand how the platform is used and how to improve it.' },
        { subtitle: 'Legal compliance', text: 'We may use your information to comply with applicable laws, respond to legal requests, or enforce our Terms of Service.' },
      ],
    },
    {
      title: 'Information Sharing',
      content: [
        { subtitle: 'Between users', text: 'When a client books a provider, basic booking information (name, service, date, time) is shared between the client and provider to fulfill the appointment.' },
        { subtitle: 'Service providers', text: 'We share data with trusted third-party services that help us operate Vyblo, including Stripe (payments), AWS (hosting and storage), SendGrid (email), and Twilio (SMS). These providers are contractually obligated to protect your data.' },
        { subtitle: 'No sale of data', text: 'We do not sell, rent, or trade your personal information to third parties for marketing purposes.' },
        { subtitle: 'Legal requirements', text: 'We may disclose your information if required by law, court order, or government authority, or to protect the rights, property, or safety of Vyblo, our users, or the public.' },
      ],
    },
    {
      title: 'Data Storage & Security',
      content: [
        { subtitle: 'Storage', text: 'Your data is stored on Amazon Web Services (AWS) servers located in the United States. We use DynamoDB, S3, and Cognito — all of which provide encryption at rest and in transit.' },
        { subtitle: 'Security measures', text: 'We implement industry-standard security practices including HTTPS encryption, secure authentication via AWS Cognito, and access controls to protect your data.' },
        { subtitle: 'Data retention', text: 'We retain your account data for as long as your account is active. If you delete your account, your personal data is removed within 30 days, except where retention is required by law.' },
      ],
    },
    {
      title: 'Your Rights & Choices',
      content: [
        { subtitle: 'Access and correction', text: 'You can view and update your personal information at any time through your account settings.' },
        { subtitle: 'Delete your account', text: 'You can delete your account from your account settings. This will cancel all upcoming bookings and remove your personal data from our systems within 30 days.' },
        { subtitle: 'Opt out of SMS', text: 'You can disable SMS notifications in your account settings at any time, or reply STOP to any SMS message we send you.' },
        { subtitle: 'Opt out of email', text: 'Transactional emails (booking confirmations, cancellations) are required for the service to function. You can opt out of non-transactional communications by updating your notification preferences.' },
        { subtitle: 'California residents (CCPA)', text: 'California residents have the right to know what personal information we collect, request deletion of their data, and opt out of the sale of personal information. We do not sell personal information.' },
      ],
    },
    {
      title: 'Cookies',
      content: [
        { subtitle: 'What we use', text: 'Vyblo uses essential cookies and local storage to keep you logged in and maintain your session. We do not use advertising or tracking cookies.' },
        { subtitle: 'Third-party cookies', text: 'Stripe may set cookies as part of their payment processing. These are governed by Stripe\'s cookie policy.' },
      ],
    },
    {
      title: 'Children\'s Privacy',
      content: [
        { subtitle: 'Age requirement', text: 'Vyblo is not directed at children under the age of 13. We do not knowingly collect personal information from children under 13. If we become aware that a child under 13 has provided us with personal information, we will delete it promptly.' },
      ],
    },
    {
      title: 'Changes to This Policy',
      content: [
        { subtitle: 'Updates', text: 'We may update this Privacy Policy from time to time. When we make significant changes, we will notify you by email or by posting a notice on the platform. Your continued use of Vyblo after changes are posted constitutes your acceptance of the updated policy.' },
      ],
    },
    {
      title: 'Contact Us',
      content: [
        { subtitle: 'Questions', text: 'If you have any questions or concerns about this Privacy Policy or how we handle your data, please contact us at privacy@vyblo.app.' },
      ],
    },
  ];

  return (
    <div style={{ minHeight: '100vh', background: 'var(--cream)', fontFamily: 'DM Sans, sans-serif' }}>

      {/* Nav */}
      <nav style={{ background: 'var(--green)', padding: '1rem 2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.4rem', fontWeight: 800, color: 'white', cursor: 'pointer' }} onClick={() => navigate('/')}>
          Vyb<span style={{ color: '#a8dfb8' }}>lo</span>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button onClick={() => navigate('/login')} style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.25)', color: 'white', borderRadius: '8px', padding: '0.4rem 1rem', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' }}>Sign In</button>
          <button onClick={() => navigate('/signup')} style={{ background: 'white', border: 'none', color: 'var(--green)', borderRadius: '8px', padding: '0.4rem 1rem', fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' }}>Sign Up</button>
        </div>
      </nav>

      {/* Hero */}
      <div style={{ background: 'var(--green)', padding: '3rem 2rem 4rem', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: -60, right: -60, width: 260, height: 260, background: 'radial-gradient(circle, rgba(255,255,255,0.06) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: -40, left: -40, width: 200, height: 200, background: 'radial-gradient(circle, rgba(74,158,107,0.4) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.25)', color: 'white', borderRadius: '50px', fontSize: '0.72rem', fontWeight: 700, padding: '0.25rem 0.875rem', marginBottom: '1rem', letterSpacing: '0.06em' }}>
            🔒 YOUR PRIVACY MATTERS
          </div>
          <h1 style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(2rem, 4vw, 2.75rem)', fontWeight: 800, color: 'white', marginBottom: '0.75rem' }}>Privacy Policy</h1>
          <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.95rem' }}>Last updated: {lastUpdated}</p>
        </div>
      </div>

      {/* Content */}
      <div style={{ maxWidth: '820px', margin: '0 auto', padding: '3rem 2rem' }}>

        {/* Intro */}
        <div style={{ background: 'var(--white)', border: '1px solid var(--border)', borderRadius: '16px', padding: '1.75rem 2rem', marginBottom: '1.5rem', boxShadow: 'var(--shadow-sm)' }}>
          <p style={{ fontSize: '0.95rem', color: 'var(--text-mid)', lineHeight: 1.8 }}>
            Vyblo ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our booking platform at <strong>vyblo.app</strong>. Please read this policy carefully. By using Vyblo, you agree to the practices described here.
          </p>
        </div>

        {/* Sections */}
        {sections.map((section, i) => (
          <div key={i} style={{ background: 'var(--white)', border: '1px solid var(--border)', borderRadius: '16px', padding: '1.75rem 2rem', marginBottom: '1rem', boxShadow: 'var(--shadow-sm)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.25rem', paddingBottom: '1rem', borderBottom: '1px solid var(--border)' }}>
              <div style={{ width: 28, height: 28, borderRadius: '8px', background: 'var(--green)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', color: 'white', fontWeight: 800, flexShrink: 0 }}>{i + 1}</div>
              <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.15rem', fontWeight: 800, color: 'var(--text)' }}>{section.title}</h2>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {section.content.map((item, j) => (
                <div key={j}>
                  <p style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--green)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.35rem' }}>{item.subtitle}</p>
                  <p style={{ fontSize: '0.9rem', color: 'var(--text-mid)', lineHeight: 1.75 }}>{item.text}</p>
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Footer links */}
        <div style={{ background: 'var(--green)', borderRadius: '16px', padding: '1.75rem 2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', marginTop: '2rem' }}>
          <div>
            <p style={{ fontFamily: 'Playfair Display, serif', fontWeight: 800, color: 'white', fontSize: '1rem', marginBottom: '0.25rem' }}>Have questions?</p>
            <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.82rem' }}>Reach out to us at privacy@vyblo.app</p>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button onClick={() => navigate('/terms')} style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.25)', color: 'white', borderRadius: '8px', padding: '0.6rem 1.25rem', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' }}>Terms of Service →</button>
            <button onClick={() => navigate('/')} style={{ background: 'white', border: 'none', color: 'var(--green)', borderRadius: '8px', padding: '0.6rem 1.25rem', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' }}>Back to Vyblo</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Privacy;
